import { sanitizeUrl } from '@braintree/sanitize-url'

/**
 * Origins allowed for externally hosted HTTP(S) downloads.
 *
 * Configure comma-separated origins in `window.APP_CONFIG.ALLOWED_DOWNLOAD_ORIGINS`, including
 * the `http://` or `https://` scheme but excluding paths, query strings, and trailing slashes.
 * The current application origin is always allowed.
 */
export const ALLOWED_DOWNLOAD_ORIGINS: ReadonlySet<string> = new Set([
    window.location.origin,
    ...window.APP_CONFIG.ALLOWED_DOWNLOAD_ORIGINS.split(',')
        .map((origin) => origin.trim())
        .filter((origin) => origin.length > 0),
])

const parseUrl = (raw: unknown): URL | null => {
    try {
        if (typeof raw !== 'string' && !(raw instanceof URL)) {
            return null
        }

        const value = String(raw)
        const sanitized = sanitizeUrl(value)
        if (!sanitized || sanitized === 'about:blank' || sanitized.startsWith('//')) {
            return null
        }

        return new URL(value, window.location.origin)
    } catch {
        return null
    }
}

const parseHttpUrl = (raw: unknown): URL | null => {
    const parsed = parseUrl(raw)
    if (!parsed || (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')) {
        return null
    }

    return parsed
}

const isInternalHttpUrl = (parsed: URL): boolean =>
    parsed.origin === window.location.origin && parsed.pathname.startsWith('/')

/**
 * Validates a runtime-provided URL for same-origin SPA navigation.
 *
 * Use this when code needs the parsed URL components before calling a router method. For a
 * navigation callback in a React component, prefer `useSafeNavigate`; use `openExternalHttp`
 * for allowlisted external downloads and `reloadInternal` for a full document reload.
 */
export const parseInternalHttpUrl = (raw: unknown): URL | null => {
    const parsed = parseHttpUrl(raw)
    return parsed && isInternalHttpUrl(parsed) ? parsed : null
}
const isRootRelativePath = (path: unknown): path is string => {
    if (typeof path !== 'string') {
        return false
    }

    return path.startsWith('/') && !path.startsWith('//') && !path.includes('\\')
}

/**
 * Opens an allowlisted external HTTP(S) URL in a new tab.
 *
 * Use this for runtime-provided download links from approved origins. Do not use `window.open`
 * directly, and do not use this for internal SPA navigation or arbitrary external links.
 */
export const openExternalHttp = (raw: unknown): void => {
    const parsed = parseHttpUrl(raw)
    if (!parsed || !ALLOWED_DOWNLOAD_ORIGINS.has(parsed.origin)) {
        return
    }

    const newWindow = window.open(parsed.href, '_blank', 'noopener,noreferrer')
    if (newWindow) {
        newWindow.opener = null
    }
}

/**
 * Performs a full document reload to a same-origin root-relative path.
 *
 * Use this only when an error or authentication flow requires a fresh application bootstrap.
 * Use `useSafeNavigate` or React Router directly for ordinary internal SPA transitions.
 */
export const reloadInternal = (path: unknown): void => {
    if (!isRootRelativePath(path)) {
        return
    }

    const parsed = parseHttpUrl(path)
    if (!parsed || !isInternalHttpUrl(parsed)) {
        return
    }

    window.location.href = path
}
